import React, { useEffect, useRef, useState } from 'react';
import { StopCircle, Play, Mic } from 'lucide-react';
import { useLiveAvatar } from '../../hooks/useLiveAvatar';
import { parseVoiceDockBlocks, voiceCardsToSegments, mergeVoiceSegments, VoiceDockSegment } from '../../utils/helpCenterBlocks';
import VoiceContentPanel from './VoiceContentPanel';
import ArticleDockPanel from './ArticleDockPanel';
import LiveAvatarGateModal from '../LiveAvatarGateModal';
import { createIdentitySync, withTimeout, IDENTITY_VERIFY_TIMEOUT_MS } from '../../utils/identityPassthrough';
import type { IdentitySync } from '../../utils/identityPassthrough';
import { newVoiceSessionId } from '../../utils/randomId';

interface VoicePlaygroundProps {
    agentId: string;
    brandColor?: string;
    theme?: 'light' | 'dark';
    voiceName?: string;
    avatarEnabled?: boolean;
    /** Plan-EffectiveLimitsAndMaintenanceBanners.md §2b (2026-05-15) —
     *  forwarded from `widget config.liveavatar_available === false`.
     *  Voice WORKS in this state (voice budget is independent); only the
     *  LiveAvatar tile shows a small "AI Avatar temporarily unavailable"
     *  inline label. When voice itself is unavailable, the parent
     *  Widget.tsx renders MaintenanceCard INSTEAD of VoicePlayground —
     *  this component never mounts in that case. */
    liveAvatarUnavailable?: boolean;
    /** Plan-VoiceWidgetRevisions Item 2 (2026-05-25) — true when this
     *  widget is running inside a `widget.js` parent iframe (a real
     *  customer embed), NOT the Playground preview and NOT a direct
     *  top-level `/widget/:id` tab. Only then can the article reader ask
     *  the parent to widen the iframe and render as a docked column
     *  beside the avatar; otherwise it falls back to a full-screen
     *  overlay. Computed by the parent Widget.tsx. */
    embedded?: boolean;
    /** Plan-CSATUIModal.md §B.3 follow-up (2026-05-15) — fire-and-
     *  forget callback that the parent (Widget.tsx) uses to open the
     *  CSAT modal when the voice tab triggers `resolve_conversation`.
     *  Required because VoicePlayground generates its OWN random
     *  session id (`session-<random>` at L243) which is different
     *  from the chat WS's session id — Storm's
     *  `_mark_conversation_resolved` → `ConnectionManager.send_
     *  personal_message(voice_session_id, ...)` only reaches the
     *  voice WS. The chat WS, registered under the FE-minted
     *  `sess_<random>` id, never receives the event during voice
     *  mode. This callback bridges the gap so Widget.tsx's parent
     *  CSAT state machine fires for both surfaces.
     *
     *  Fires AFTER the existing teardown chain (stopSession +
     *  status flip + 2-second fallback) settles — purely additive,
     *  does not gate / reorder / replace any teardown step. The
     *  auto-disconnect protected feature is unaffected.
     *
     *  Callback param: the conversation_id (= voice WS's
     *  `session_id`) that the conversation doc lives under in the
     *  DB. Required because VoicePlayground mints its own session id
     *  at L243 which doesn't match the chat WS's id; passing it
     *  through lets Widget.tsx POST CSAT to the correct
     *  `conversation_id` (otherwise the endpoint 404s with "not
     *  found"). Read from the `conversation_ended` frame's
     *  `session_id` field (broadcast shape locked in
     *  `back/api/voice.py:570`), with a fallback to the voice WS's
     *  own minted id stored in a ref. */
    onConversationEnded?: (conversationId: string | null) => void;
    /** Contract-WidgetEmailOTP.md — fire-and-forget callback the parent
     *  (Widget.tsx) uses to open the email-OTP modal when a gated
     *  customer/order tool is called during a VOICE turn.
     *
     *  Needed for exactly the same reason as `onConversationEnded` above: the
     *  voice WS runs under its own minted `session-<random>` id, so the flag
     *  arrives here and never on the chat WS. It matters more here than for
     *  CSAT, because the backend keys verification on `otp:verified:
     *  {session_id}` — verifying under the CHAT session id would not unlock
     *  the tool for the VOICE session. Hence the param: the parent must send
     *  the VOICE session id to /verify/request and /verify/confirm.
     *
     *  Purely additive — opens a UI overlay and touches no part of the
     *  session, teardown, or auto-disconnect chain. The code is TYPED in that
     *  modal; it is never spoken and never read aloud.
     *
     *  🚨 `reason` carries `verification_reason` off the same frame. The two
     *  values need OPPOSITE escapes — `order_lookup` means verify THIS address,
     *  `different_email` means the caller IS verified and the order is under
     *  another one, so re-confirming what they hold can never help. Optional
     *  and defaulted by the parent, so a server that omits the field behaves
     *  exactly as before. */
    onVerificationRequired?: (
        voiceSessionId: string,
        reason?: 'order_lookup' | 'different_email',
    ) => void;
    /** Plan-VoiceCartRoundTrip — true only for a REAL Shopify embed (the
     *  one surface that has a same-origin storefront cart + a widget.js
     *  parent bridge). When true we append `embedded=1` to the voice WS
     *  URL so the BE registers the long-running storefront cart tools.
     *  Playground preview and standalone tabs leave this false → the
     *  voice agent gets NO cart tools (unchanged behavior). */
    shopifyStorefront?: boolean;
    /** Plan-VoiceCartRoundTrip — executes a storefront action via the
     *  parent Widget.tsx bridge (executeViaParent → window.parent
     *  postMessage → theme extension → /cart/add.js) and resolves the
     *  normalized outcome. VoicePlayground then sends that result back
     *  over the VOICE WS as a `tool_result` frame correlated by
     *  function_call_id. Only invoked when `shopifyStorefront` is true. */
    onStorefrontAction?: (
        action: string,
        payload: any,
    ) => Promise<{
        status: 'success' | 'error';
        result: any;
        error_code: string | null;
        error_message: string | null;
    }>;
    /** Identity passthrough — Widget.tsx's ref holding the partner host page's
     *  identity token (a bearer credential: read at use time, never copied into
     *  state, never logged). A ref rather than a value prop so the token never
     *  flows through React props/DevTools. Absent (Playground / standalone) ⇒
     *  voice behaves exactly as before. */
    identityTokenRef?: React.MutableRefObject<string | null>;
    /** Bumped by Widget.tsx on every `identify()` from the host. While a voice
     *  session is live, a change re-verifies (or clears) the VOICE session id. */
    identityRevision?: number;
}

const INPUT_SAMPLE_RATE = 16000;
const OUTPUT_SAMPLE_RATE = 24000;

const base64PcmToFloat32 = (b64: string): Float32Array => {
    const binary = atob(b64);
    const len = binary.length;
    const byteLen = len - (len % 2);
    const int16 = new Int16Array(byteLen / 2);
    for (let i = 0; i < int16.length; i++) {
        int16[i] = (binary.charCodeAt(i * 2) | (binary.charCodeAt(i * 2 + 1) << 8)) << 16 >> 16;
    }
    const float32 = new Float32Array(int16.length);
    for (let i = 0; i < int16.length; i++) float32[i] = int16[i] / 0x8000;
    return float32;
};

const VoicePlayground: React.FC<VoicePlaygroundProps> = ({ agentId, brandColor = '#1e73be', theme = 'light', voiceName, avatarEnabled = true, liveAvatarUnavailable = false, embedded = false, onConversationEnded, onVerificationRequired, shopifyStorefront = false, onStorefrontAction, identityTokenRef, identityRevision }) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [status, setStatus] = useState<string>('Ready');
    const [voiceContent, setVoiceContent] = useState<VoiceDockSegment[]>([]);
    // Voice card re-display fix (2026-06-30) — the cards panel is hidden when
    // the user dismisses it (X / mobile drag handle), but `voiceContent` is
    // NOT cleared so the agent can re-surface the same cards on "send it
    // again". Every incoming `voice_cards` batch flips this back to false, so
    // a re-send re-opens the panel. Panel visibility = content && !dismissed.
    const [panelDismissed, setPanelDismissed] = useState(false);
    // Plan-VoiceWidgetRevisions Item 2 (2026-05-25) — slug of the article
    // the user is currently reading (via a card's "Read"), lifted here from
    // VoiceContentPanel so the opened article renders as a sibling of the
    // avatar (docked column beside it, or full-screen overlay) instead of
    // shrinking the avatar. null = showing cards / nothing.
    const [viewingSlug, setViewingSlug] = useState<string | null>(null);
    // `avatarActive` mirrors `avatarEnabled` initially, but flips to `false`
    // when the avatar connect path fails (throw OR entitlement gate). The
    // ref shadow lets `playAudioChunk` (which runs from WS frames outside
    // the render closure) route to the right output without waiting for a
    // React re-render to propagate the flip. Both must move together —
    // `setAvatarActive(false)` calls update the ref synchronously below.
    //
    // Plan-EffectiveLimitsAndMaintenanceBanners.md §2b — when
    // `liveAvatarUnavailable` is true (workspace's LA budget exhausted),
    // we force-disable the avatar surface even if `avatarEnabled=true`
    // from agent config. Voice continues to work; only the avatar tile
    // is swapped for the degraded-label box below.
    const effectiveAvatarEnabled = avatarEnabled && !liveAvatarUnavailable;
    const [avatarActive, setAvatarActive] = useState<boolean>(effectiveAvatarEnabled);
    const avatarActiveRef = useRef<boolean>(effectiveAvatarEnabled);

    const websocketRef = useRef<WebSocket | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const workletNodeRef = useRef<AudioWorkletNode | null>(null);
    const playbackContextRef = useRef<AudioContext | null>(null);
    const playbackTimeRef = useRef<number>(0);
    // Diagnostic counters for the 1007 audio-format audit (no behavior impact)
    const chunkCountRef = useRef<number>(0);

    const liveAvatar = useLiveAvatar();
    // Task #17 — stale-closure bug fix. The `liveAvatar` object captured
    // inside `stopSession()` / `teardownLiveAvatar()` comes from the
    // render closure that registered the WS message handler. By the time
    // a `conversation_ended` frame arrives many turns later, the hook
    // has re-rendered with new state but the closure still holds the
    // old `liveAvatar` reference — `liveAvatar.isConnected` reads stale.
    // If it reads `false`, the guarded `disconnect()` is skipped, the
    // HeyGen session keeps streaming, credits keep burning.
    //
    // Mirror the live `liveAvatar` into a ref on every render. WS-frame
    // callbacks must read `liveAvatarRef.current` — never the closure
    // `liveAvatar`. `disconnect()` is idempotent (guarded inside the
    // hook by `disconnectedRef`), so we drop the `isConnected ||
    // isConnecting` gate entirely and always call it.
    const liveAvatarRef = useRef(liveAvatar);
    useEffect(() => { liveAvatarRef.current = liveAvatar; }, [liveAvatar]);

    // Plan-CSATUIModal.md §B.3 follow-up — ref shadow for the
    // `onConversationEnded` prop. Matches the `liveAvatarRef` pattern:
    // the WS `onmessage` handler is registered inside `startSession()`
    // at click-time, so its closure captures `onConversationEnded`
    // from that render. If Widget.tsx re-renders with a fresh callback
    // before the `conversation_ended` frame arrives, the closure would
    // hold a stale reference. Reading from `.current` at fire time
    // dodges that.
    const onConversationEndedRef = useRef(onConversationEnded);
    useEffect(() => { onConversationEndedRef.current = onConversationEnded; }, [onConversationEnded]);

    // Same ref treatment for the OTP callback, and for the same reason: the WS
    // `onmessage` handler is registered inside `startSession()` at click-time,
    // so its closure would otherwise capture the mount-time prop forever.
    const onVerificationRequiredRef = useRef(onVerificationRequired);
    useEffect(() => { onVerificationRequiredRef.current = onVerificationRequired; }, [onVerificationRequired]);

    // Plan-VoiceCartRoundTrip — hoist the storefront-action bridge callback
    // to a ref so the WS `onmessage` closure (bound once at startSession
    // time) always calls the freshest prop, dodging stale-closure capture.
    const onStorefrontActionRef = useRef(onStorefrontAction);
    useEffect(() => { onStorefrontActionRef.current = onStorefrontAction; }, [onStorefrontAction]);

    // Plan-CSATUIModal.md §B.3 follow-up — voice WS's minted session
    // id (`session-<random>` from `startSession()` at ~L283) hoisted
    // to a ref so the `conversation_ended` handler can fall back to
    // it when the frame payload's `session_id` field is missing.
    // Primary path is `message.session_id` from the frame (matches
    // backend `voice.py:570` broadcast shape); ref is the
    // belt-and-suspenders.
    const voiceSessionIdRef = useRef<string | null>(null);

    // Identity passthrough — voice's own verify/clear queue, keyed on the VOICE
    // session id above (the backend keys verified identity by session id, so
    // the chat session's verification does not cover a voice/avatar call).
    const identitySyncRef = useRef<IdentitySync | null>(null);
    if (!identitySyncRef.current) identitySyncRef.current = createIdentitySync();

    // Plan-LiveAvatarLifecycleAndCreditsFallback §A.2.1 — extracted
    // teardown helper. Previously the disconnect lived inline inside
    // stopSession(), and the WS error/close paths only called
    // stopAudioCapture() — leaving the HeyGen session streaming dead
    // frames until either the keepalive timed out (~30s) or the user
    // reloaded. That bled credits against our LiveAvatar subscription.
    // Now every WS exit path calls this helper; combined with the
    // disconnectedRef guard inside useLiveAvatar, double-calls are a
    // safe no-op.
    //
    // Task #17 — read from the ref (not the closure), and always call
    // disconnect unconditionally. The hook is idempotent; the guard was
    // the bug, not safety.
    const teardownLiveAvatar = () => {
        try {
            liveAvatarRef.current?.disconnect();
            console.log('[VoicePlayground.teardownLiveAvatar] disconnect called via ref');
        } catch (e) {
            console.warn('[VoicePlayground.teardownLiveAvatar] LiveAvatar disconnect failed', e);
        }
    };

    // Idempotent fallback transition. Called from BOTH the connect-catch
    // path (an SDK or HeyGen API throw) AND the gate-useEffect path (the
    // 403-with-detail case where `useLiveAvatar.connect` SWALLOWS the
    // error and just sets `gate`). Either path leaves us with no avatar
    // session — keep the WS alive but reroute PCM frames to a plain
    // playback AudioContext (lazy-inited in `playAudioChunk` so there's
    // no race with frames arriving before the React state propagates).
    //
    // Plan-LiveAvatarLifecycleAndCreditsFallback §B.2.2 — added the
    // `platform-exhausted` reason to surface a more specific status
    // ("AI Avatar temporarily unavailable — using voice-only mode")
    // when the gate code is `platform_credits_exhausted` (Prodagi's
    // master HeyGen account ran out of credits, not the customer's
    // per-workspace addon).
    const fallbackToPlainVoice = (
        reason: 'thrown' | 'gated' | 'mid-session-gated' | 'platform-exhausted',
    ) => {
        if (!avatarActiveRef.current) return;
        avatarActiveRef.current = false;
        setAvatarActive(false);
        const msg = reason === 'platform-exhausted'
            ? 'AI Avatar temporarily unavailable — using voice-only mode'
            : 'AI Avatar unavailable — using plain voice';
        setStatus(msg);
        // Plan-VoiceWidgetRevisions Item 3 follow-up (2026-05-25) — fallback
        // safety: if for any reason `msg` above didn't apply and we were left
        // on the avatar-mode 'Connecting...' pill, promote to 'Connected' so
        // the status never sticks on connecting when plain voice is actually
        // working. Guarded so the specific `msg` (set just above) and any
        // terminal status win — only a lingering 'Connecting...' is promoted.
        setStatus((prev) => (prev === 'Connecting...' ? 'Connected' : prev));
        console.warn('[VoicePlayground] Falling back to plain voice path:', reason);
    };

    // Watch the gate. Triggers BOTH on initial connect (when the 403 path
    // sets `gate` without throwing) and on mid-session gate transitions
    // (entitlement revoked while live — currently not emitted by the
    // backend but defensive against future minute-cap pushes).
    useEffect(() => {
        if (liveAvatar.gate && avatarActiveRef.current) {
            // Plan-LiveAvatarLifecycleAndCreditsFallback §B.2.2 — pick
            // the most specific reason. Platform exhaustion has its own
            // copy so customers see "temporarily unavailable" rather
            // than the generic "unavailable" (the former implies
            // self-resolution, the latter implies they should fix it).
            const reason: 'platform-exhausted' | 'mid-session-gated' | 'gated' =
                liveAvatar.gate.code === 'platform_credits_exhausted'
                    ? 'platform-exhausted'
                    : (avatarActiveRef.current && isConnected ? 'mid-session-gated' : 'gated');
            fallbackToPlainVoice(reason);
            // Plan-VoiceWidgetRevisions Item 3 follow-up (2026-05-25) —
            // fallback safety at the gate path too: don't leave the status
            // stuck on the avatar-mode 'Connecting...' pill when a gate
            // (credits/platform) dropped us to plain voice. Guarded so the
            // specific gate/fallback message set inside fallbackToPlainVoice
            // (and any terminal status) is preserved — only a lingering
            // 'Connecting...' is promoted.
            setStatus((prev) => (prev === 'Connecting...' ? 'Connected' : prev));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [liveAvatar.gate]);

    // Plan-VoiceWidgetRevisions Item 3 follow-up (2026-05-25) — promote the
    // status pill from 'Connecting...' to 'Connected' once the live-avatar
    // session is actually live, so the top status matches the avatar overlay
    // (which clears "Connecting to Agent" off the same `liveAvatar.isConnected`
    // flag). Guard with `prev === 'Connecting...'` so we NEVER clobber a more
    // specific terminal status — 'Ended', an error/gate message, or a
    // plain-voice fallback message that may have already been set.
    useEffect(() => {
        if (liveAvatar.isConnected) {
            setStatus((prev) => (prev === 'Connecting...' ? 'Connected' : prev));
        }
    }, [liveAvatar.isConnected]);

    // Plan-LiveAvatarLifecycleAndCreditsFallback §A.2.3 — beforeunload
    // guard. The unmount useEffect cleanup runs reliably on
    // React-router navigations, but is not guaranteed during hard
    // browser tab close depending on iframe lifecycle. Defense-in-depth.
    useEffect(() => {
        const onBeforeUnload = () => teardownLiveAvatar();
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Identity passthrough — the host called `identify()` (refresh or logout)
    // while a voice session may be live: re-verify, or clear, under the VOICE
    // session id. No live WS ⇒ nothing to do; the next startSession verifies
    // with whatever token is held then. A clear only reaches the network for
    // a session this queue actually sent a token for.
    useEffect(() => {
        if (identityRevision === undefined) return;
        const voiceSessionId = voiceSessionIdRef.current;
        if (!voiceSessionId || !websocketRef.current || !agentId) return;
        void identitySyncRef.current!.push(voiceSessionId, agentId, identityTokenRef?.current ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [identityRevision]);

    // Widget-close signal from the parent launcher (Fix B). The capture
    // page POSTs `{type:'prodagi_widget_close'}` when the user clicks the
    // launcher to hide the panel — we need to tear down the live voice/
    // avatar session immediately so the mic indicator drops and the
    // HeyGen session releases. Origin check uses `event.source` because
    // the launcher posts with `'*'` target and we can't pin a known
    // parent origin string.
    useEffect(() => {
        const handler = (event: MessageEvent) => {
            if (event.source !== window.parent) return;
            const data = event.data;
            if (!data || typeof data !== 'object') return;
            if (data.type === 'prodagi_widget_close') {
                stopSession();
                // Plan-VoiceWidgetRevisions Item 2 — collapse the side dock
                // when the launcher hides the widget. Clearing both
                // `viewingSlug` and `voiceContent` flips `dockActive` false,
                // and the centralized dock effect below posts
                // `prodagi_widget_dock_collapse` so the iframe width is
                // restored (otherwise it would reopen still widened).
                setViewingSlug(null);
                setVoiceContent([]);
                setPanelDismissed(false);
            }
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Cards/avatar usability (2026-06-29) — SINGLE SOURCE OF TRUTH for the
    // iframe dock-widen. Previously only ArticleDockPanel asked the parent
    // widget.js to widen the iframe (on mount/unmount), so the COMPACT CARDS
    // panel (product / order / help-center) was never given room — with the
    // LiveAvatar tile (256px) it had no space in a ~380px embed. Lift the
    // widen here so it fires whenever a side panel is present (cards OR an
    // opened article) during a live session, and collapses when it clears,
    // the session ends, or this component unmounts (widget close / teardown).
    //
    // Keyed on the derived `dockActive` boolean so switching between the
    // cards panel and an opened article (both keep a panel present) does NOT
    // collapse→re-expand and flicker the iframe. Desktop + embedded only —
    // mobile uses the full-width bottom sheet / overlay and must NOT dock.
    const [isDesktop, setIsDesktop] = useState<boolean>(
        typeof window !== 'undefined' ? window.innerWidth >= 480 : true,
    );
    useEffect(() => {
        const onResize = () => setIsDesktop(window.innerWidth >= 480);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    // `extra` to add to the base widget width. 360 fits BOTH the 360px docked
    // article column AND the 288px cards panel, so toggling between them never
    // needs a second expand post (avoids flicker). widget.js clamps the total
    // to the viewport, so a narrow desktop just squeezes the column.
    const DOCK_EXTRA = 360;
    const dockActive =
        embedded && isDesktop && isConnected &&
        (viewingSlug !== null || (voiceContent.length > 0 && !panelDismissed));
    useEffect(() => {
        if (!dockActive) return;
        const post = (msg: object) => {
            try {
                window.parent && window.parent.postMessage(msg, '*');
            } catch {
                /* fail silent — falls back to a clamped on-screen column */
            }
        };
        post({ type: 'prodagi_widget_dock_expand', extra: DOCK_EXTRA });
        return () => {
            post({ type: 'prodagi_widget_dock_collapse' });
        };
    }, [dockActive]);
    // Single-avatar deployment — operator-configured via backend env.
    // The prior `getVoiceGender(voiceName) ?? 'Male'` lookup is gone;
    // preview image is hardcoded below to the active avatar's PNG.
    // `voiceName` prop kept for future use (already wired through
    // the playground tree).
    void voiceName;

    useEffect(() => {
        return () => {
            stopSession();
        };
    }, []);

    const startSession = async () => {
        try {
            setStatus('Connecting...');
            chunkCountRef.current = 0;

            const AudioContextClass = (window.AudioContext || (window as any).webkitAudioContext);
            const ctx = new AudioContextClass({ sampleRate: INPUT_SAMPLE_RATE });
            audioContextRef.current = ctx;
            // [1007 fix] The `sampleRate` constructor arg is a HINT — Firefox
            // and some Safari builds silently fall back to the device-native
            // rate (typically 48000 Hz on laptops, 44100 on some desktops),
            // which previously caused us to ship 48 kHz bytes labelled
            // "audio/pcm;rate=16000" → Gemini Live 1007. The worklet below
            // now resamples to 16 kHz from whatever native rate we actually
            // got. This warn/log split makes the fallback path easy to spot
            // in user reports.
            if (ctx.sampleRate !== INPUT_SAMPLE_RATE) {
                console.warn(
                    '[VoicePlayground] AudioContext fell back to', ctx.sampleRate,
                    'Hz — resampling to', INPUT_SAMPLE_RATE, 'in worklet (state:', ctx.state, ')',
                );
            } else {
                console.log(
                    '[VoicePlayground] AudioContext at native', ctx.sampleRate,
                    'Hz — no resampling needed (state:', ctx.state, ')',
                );
            }

            // Task #17 Bug B — hoist `sessionId` generation BEFORE the
            // avatar connect call so we can pass it through as the
            // backend `conversation_id`. Storm's BE registers a Redis
            // mapping `voice:conv:<sessionId>:heygen_session = <heygen
            // session id>` keyed off this value, letting server-side
            // teardown resolve the HeyGen REST id when the voice WS
            // closes. Previously the mapping was missing → BE-initiated
            // HeyGen stops 404'd → vendor room stayed up → credits
            // burned.
            //
            // The voice WS URL still uses the same sessionId in its
            // path segment (line below), so the conversation_id we
            // hand to the avatar-session POST is the EXACT same string
            // the backend will see when the WS opens.
            // 🚨 Full-entropy (128-bit CSPRNG) — the voice WS accepts any id, so
            // the id is the only thing guarding this session; see utils/randomId.ts.
            const sessionId = newVoiceSessionId();
            // Plan-CSATUIModal.md §B.3 follow-up — expose for the
            // CSAT callback fallback (in case the frame payload's
            // `session_id` arrives missing).
            voiceSessionIdRef.current = sessionId;

            // Connect LiveAvatar only when the avatar surface is enabled.
            // `avatarActiveRef` is the live source-of-truth — the gate
            // useEffect could have flipped it false between renders.
            //
            // Plan-VoiceWidgetRevisions Item 3 (2026-05-25) — connect
            // latency quick-win. Previously this `await`ed the FULL
            // HeyGen flow (token POST + SDK `session.start()` WebRTC
            // handshake, ~2–15s) BEFORE the voice WS below was even
            // created, fully serializing the Gemini Live connect behind
            // HeyGen. We now FIRE the avatar connect WITHOUT awaiting it
            // and fall straight through to open the voice WS, so the two
            // slow third-party handshakes (HeyGen WebRTC + Gemini Live)
            // run concurrently. This changes no API contract — both
            // calls already received the same `sessionId` / `agentId`.
            //
            // Correctness notes:
            //   - The catch still runs (attached as `.catch` on the
            //     non-awaited promise) → `fallbackToPlainVoice('thrown')`
            //     reroutes PCM to plain-voice playback exactly as before.
            //   - The 403 entitlement gate is swallowed INSIDE
            //     `connect()` (sets `liveAvatar.gate`); the existing gate
            //     useEffect handles the fallback — unaffected by removing
            //     the await.
            //   - Teardown (stopSession / teardownLiveAvatar) reads
            //     `liveAvatarRef.current` and is idempotent; the hook's
            //     `disconnectedRef` guard safely handles a disconnect
            //     that races an in-flight connect.
            //   - `sendAudio` no-ops until the SDK session reaches
            //     CONNECTED, so PCM frames that arrive before the avatar
            //     finishes connecting are dropped safely (same as before
            //     — the old serialized path could also receive frames
            //     before SESSION_STREAM_READY).
            if (avatarActiveRef.current) {
                liveAvatar.connect(agentId, sessionId).catch((err) => {
                    console.error('LiveAvatar connection failed:', err);
                    fallbackToPlainVoice('thrown');
                });
            }
            // Plain-voice playback context is now lazy-inited inside
            // `playAudioChunk` the first time a frame arrives on the
            // fallback path. That removes the timing race where a frame
            // could land between the gate flip and a synchronous init
            // here. The `avatarEnabled=false` start-state still works
            // because the lazy init fires on the first frame regardless
            // of how avatarActive transitioned to false.

            // Identity passthrough — verify the host's token under THIS voice
            // session id BEFORE the voice WS opens, so the very first tool call
            // already sees the identity. Bounded by a timeout and never throws:
            // a failed or slow verify still starts the call (the agent will ask
            // the buyer to sign in). The avatar connect above keeps running
            // concurrently, so this does not serialize HeyGen behind it.
            const identityToken = identityTokenRef?.current ?? null;
            if (identityToken && agentId) {
                await withTimeout(
                    identitySyncRef.current!.push(sessionId, agentId, identityToken),
                    IDENTITY_VERIFY_TIMEOUT_MS,
                    false,
                );
                // Stopped (Stop button / launcher close) while verifying —
                // stopSession already tore everything down; don't open a WS.
                if (audioContextRef.current !== ctx) return;
                // The host re-identified (refresh or logout) during the await —
                // apply the newer intent too. Non-blocking; the queue orders it.
                const latestToken = identityTokenRef?.current ?? null;
                if (latestToken !== identityToken) {
                    void identitySyncRef.current!.push(sessionId, agentId, latestToken);
                }
            }

            const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8000';
            const wsBaseUrl = apiUrl.replace(/^http/, 'ws');
            // Plan-VoiceCartRoundTrip — `embedded=1` only for a real Shopify
            // embed (same-origin storefront cart + widget.js parent bridge).
            // The BE gates registration of the long-running storefront cart
            // tools on this param AND an active Shopify install. Playground /
            // standalone omit it → no cart tools (unchanged behavior).
            const embeddedParam = shopifyStorefront ? '&embedded=1' : '';
            const wsUrl = `${wsBaseUrl}/api/voice/ws/${sessionId}?agent_id=${agentId || ''}&source=playground${embeddedParam}`;

            const ws = new WebSocket(wsUrl);
            websocketRef.current = ws;

            ws.onopen = () => {
                // Plan-VoiceWidgetRevisions Item 3 follow-up (2026-05-25) —
                // honest status. Item 3 opened the voice WS concurrently with
                // the HeyGen avatar connect, so the WS now opens BEFORE the
                // avatar is live. Showing 'Connected' here contradicted the
                // avatar overlay still reading "Connecting to Agent". In
                // avatar mode stay 'Connecting...' (blue, matches the overlay)
                // until `liveAvatar.isConnected` flips true (promoted by the
                // useEffect below); plain-voice mode keeps 'Connected' as
                // before. The early-WS optimization itself is unchanged.
                setStatus(avatarActiveRef.current ? 'Connecting...' : 'Connected');
                setIsConnected(true);
                startAudioCapture();
            };

            ws.onmessage = async (event) => {
                const message = JSON.parse(event.data);

                // Contract-WidgetEmailOTP.md — a gated customer/order tool was
                // called without a verified email. Hand up the session id the
                // BACKEND names in the frame: its gate keys on
                // `otp:verified:{session_id}`, and in voice that id is the
                // conversation session, which is NOT necessarily our locally
                // minted `session-<random>`. Verifying against the wrong one
                // would close the modal on a correct code while leaving the
                // tool locked — silent, and only visible on a live call. The
                // local ref is a fallback for a server predating the field.
                // Not a `return`: the flag rides alongside a normal frame,
                // which must still be handled below. Absent/false ⇒ nothing
                // changes.
                if (message.verification_required === true) {
                    try {
                        onVerificationRequiredRef.current?.(
                            (typeof message.session_id === 'string' && message.session_id) ||
                            voiceSessionIdRef.current ||
                            '',
                            // 🚨 Anything but the literal `different_email`
                            // falls back to `order_lookup` — including a frame
                            // that omits the field. Guessing the other way
                            // would tell a caller who simply has not verified
                            // yet to go find a different address.
                            message.verification_reason === 'different_email'
                                ? 'different_email'
                                : 'order_lookup',
                        );
                    } catch (err) {
                        console.warn('[VoicePlayground] onVerificationRequired callback threw', err);
                    }
                }

                // Structured error frame from backend (Storm 3991d6a). Backend
                // emits {"type":"error","code":...,"message":...} just before
                // closing the WS on a Gemini Live failure. Without this branch
                // the user would just see "Disconnected" and have to refresh —
                // we now surface a specific status so they can click Start to
                // restart cleanly. No auto-retry (avoids reconnect storms).
                if (message.type === 'error') {
                    console.error('[VoicePlayground] backend error', message);
                    if (message.code === 'live_audio_format') {
                        setStatus('Audio format error. Click to restart.');
                    } else if (message.code === 'live_disconnect') {
                        setStatus('Audio session ended unexpectedly. Click to restart.');
                    } else {
                        setStatus('Audio session ended. Click to restart.');
                    }
                    setIsConnected(false);
                    stopAudioCapture();
                    // Plan-LiveAvatarLifecycleAndCreditsFallback §A.2.2
                    // — was a credit leak: structured-error frame closed
                    // the chat/voice WS but left the HeyGen avatar
                    // session streaming dead frames.
                    teardownLiveAvatar();
                    return;
                }

                // Plan-EndConversationUX §B.1 — server broadcasts when
                // the agent calls `resolve_conversation` / `record_csat`
                // (gated by `turn_complete` so the spoken farewell line
                // finishes first). This is the deterministic disconnect
                // signal — perform a TOTAL teardown by delegating to
                // `stopSession()` (closes WS, mic, audio contexts,
                // avatar, playback context), then override the status
                // pill back to "Ended" since stopSession resets it to
                // "Ready". Subsequent ws.onclose firings re-hit
                // idempotent teardown so double-call is safe.
                if (message.type === 'conversation_ended') {
                    console.log('[VoicePlayground] conversation_ended frame:', message);
                    // Plan-CSATUIModal.md §B.3 follow-up — capture
                    // the conversation_id from the frame payload
                    // BEFORE stopSession() / 2s fallback, so the
                    // value is stable through the 2-second wait.
                    // `voice.py:570` broadcasts `{type, session_id,
                    // reason, ended_at}`; `session_id` here is the
                    // DB conversation_id (= the voice WS minted id).
                    // Fall back to `voiceSessionIdRef.current` if
                    // the field is somehow missing.
                    const conversationIdForCsat: string | null =
                        (message && typeof message.session_id === 'string' && message.session_id) ||
                        voiceSessionIdRef.current ||
                        null;
                    stopSession();
                    setStatus('Ended');

                    // Task #17 — defense-in-depth fallback. If anything
                    // inside stopSession() bailed silently (a thrown
                    // disconnect that got swallowed, a state read that
                    // returned wrong, an async close that didn't settle),
                    // this re-checks 2 seconds later and forces hard
                    // teardown. The user's symptom is "model keeps
                    // talking after farewell line" — that's only
                    // possible if the WS stays OPEN; this catches it.
                    setTimeout(() => {
                        const wsStillOpen = !!websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN;
                        const avatarStillUp = !!liveAvatarRef.current?.isConnected;
                        if (avatarStillUp) {
                            console.warn('[VoicePlayground] avatar STILL connected 2s after conversation_ended — forcing hard teardown');
                            try { liveAvatarRef.current?.disconnect(); } catch { /* ignore */ }
                        }
                        if (wsStillOpen) {
                            console.warn('[VoicePlayground] WS STILL open 2s after conversation_ended — forcing close');
                            try { websocketRef.current?.close(1000, 'fallback teardown'); } catch { /* ignore */ }
                        }
                        if (!avatarStillUp && !wsStillOpen) {
                            console.log('[VoicePlayground] 2s fallback check passed — teardown is clean');
                        }
                        // Plan-CSATUIModal.md §B.3 follow-up — fire the
                        // optional parent callback at the TAIL of the
                        // 2-second fallback so:
                        //   1. stopSession() (line above) has had time to
                        //      settle WS close + mic stop + audio context
                        //      close + initial avatar disconnect.
                        //   2. The 2-second fallback above has caught any
                        //      stuck WS / avatar, force-closing them.
                        //   3. THEN the callback fires — by which point
                        //      the auto-disconnect chain is fully done.
                        // Wrapped in try/catch so a callback throw can't
                        // mask the teardown log or leak an unhandled
                        // rejection. Read from the ref to dodge stale-
                        // closure capture (the WS onmessage closure was
                        // bound at startSession() time).
                        try {
                            onConversationEndedRef.current?.(conversationIdForCsat);
                        } catch (err) {
                            console.warn('[VoicePlayground] onConversationEnded callback threw', err);
                        }
                    }, 2000);
                    return;
                }

                // Plan-HumanAgentHandoff-2026-06-09 §AI suppression gate
                // / Voice / LiveAvatar special handling — when a human
                // staff member takes over mid-call we must release the
                // HeyGen avatar (don't pay for idle minutes) and hand
                // the customer back to the text widget. The customer-
                // visible notice fades the audio status pill to
                // "Human agent has joined — switching to text" and
                // calls the parent `onConversationEnded` callback
                // (Widget.tsx unmounts the voice tab into chat mode).
                //
                // We mirror the `conversation_ended` teardown chain
                // exactly — same stopSession() + 2s fallback — because
                // the failure mode is the same (silent HeyGen leak).
                // Only the status string differs.
                if (message.type === 'human_handoff_status' && message.status === 'active') {
                    console.log('[VoicePlayground] human_handoff_status active — graceful disconnect', message);
                    const staffName = message.staff_name as string | undefined;
                    const noticeName = staffName ? `${staffName} has joined` : 'Human agent has joined';
                    setStatus(`${noticeName} — switching to text`);
                    stopSession();
                    setTimeout(() => {
                        const wsStillOpen = !!websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN;
                        const avatarStillUp = !!liveAvatarRef.current?.isConnected;
                        if (avatarStillUp) {
                            console.warn('[VoicePlayground] avatar STILL connected 2s after handoff — forcing hard teardown');
                            try { liveAvatarRef.current?.disconnect(); } catch { /* ignore */ }
                        }
                        if (wsStillOpen) {
                            console.warn('[VoicePlayground] WS STILL open 2s after handoff — forcing close');
                            try { websocketRef.current?.close(1000, 'human handoff'); } catch { /* ignore */ }
                        }
                        // Reuse the existing parent-callback contract:
                        // Widget.tsx already swaps back into chat mode
                        // when the voice tab signals "done" via this
                        // callback. Passing `null` skips the CSAT
                        // modal — handoff is not a resolution.
                        try {
                            onConversationEndedRef.current?.(null);
                        } catch (err) {
                            console.warn('[VoicePlayground] onConversationEnded callback threw (handoff path)', err);
                        }
                    }, 2000);
                    return;
                }

                // Plan-VoiceCartRoundTrip — the embedded-voice agent called a
                // long-running storefront cart tool. The BE deferred the tool
                // response and emitted this frame on the VOICE WS. Mirror the
                // chat handler (Widget.tsx storefront_action branch): execute
                // the action through the parent bridge, then send the REAL
                // result back over THIS voice WS as a `tool_result` frame
                // correlated by function_call_id, so the model confirms the
                // outcome truthfully. Contract:
                //   BE→FE: {type:'storefront_action', action, token, payload,
                //           function_call_id}
                //   FE→BE: {type:'tool_result', function_call_id, status,
                //           result}
                if (message.type === 'storefront_action' && message.action) {
                    const fnCallId: string | null = message.function_call_id ?? null;
                    const bridge = onStorefrontActionRef.current;
                    if (!bridge) {
                        // No parent bridge wired (not a real Shopify embed) —
                        // report an error back so the agent doesn't hang on a
                        // dispatched-but-never-answered turn.
                        try {
                            websocketRef.current?.send(JSON.stringify({
                                type: 'tool_result',
                                function_call_id: fnCallId,
                                status: 'error',
                                result: {
                                    error_code: 'no_storefront_bridge',
                                    error_message: 'Storefront bridge unavailable in this surface',
                                },
                            }));
                        } catch (err) {
                            console.warn('[VoicePlayground] tool_result send failed (no bridge)', err);
                        }
                        return;
                    }
                    // Fire the bridge and POST the outcome back. Not awaited in
                    // a way that blocks the handler — but we DO await the
                    // promise inside this async onmessage so the result lands
                    // as soon as the parent replies (≤15s bridge timeout).
                    bridge(message.action as string, message.payload || {})
                        .then((outcome) => {
                            try {
                                websocketRef.current?.send(JSON.stringify({
                                    type: 'tool_result',
                                    function_call_id: fnCallId,
                                    status: outcome.status,
                                    result:
                                        outcome.status === 'success'
                                            ? outcome.result
                                            : {
                                                error_code: outcome.error_code,
                                                error_message: outcome.error_message,
                                            },
                                }));
                            } catch (err) {
                                console.warn('[VoicePlayground] tool_result send failed', err);
                            }
                        })
                        .catch((err) => {
                            console.warn('[VoicePlayground] storefront bridge threw', err);
                            try {
                                websocketRef.current?.send(JSON.stringify({
                                    type: 'tool_result',
                                    function_call_id: fnCallId,
                                    status: 'error',
                                    result: {
                                        error_code: 'bridge_exception',
                                        error_message: String(err),
                                    },
                                }));
                            } catch {
                                /* WS already closed — agent turn will time out */
                            }
                        });
                    return;
                }

                // Plan-VoiceServerSideCards (2026-06-29) — voice cards are now
                // pushed server-side over the voice WS instead of being parsed
                // out of the spoken transcript. The Gemini Live model no longer
                // emits `[PRODUCT_CARD]`/`[ORDER_CARD]`/… markers in voice (it
                // would read the marker JSON aloud + loop); the backend sends
                // the rendered card data out-of-band as it gets each tool
                // result. We remap kind→segment and append to the SAME
                // `voiceContent` the panel already renders, de-duped by the
                // card's natural id so re-pushes don't double-render.
                if (message.type === 'voice_cards') {
                    const segs = voiceCardsToSegments(message.cards || []);
                    if (segs.length > 0) {
                        // Per-batch upsert (NOT history-dedup) so a re-sent
                        // card re-surfaces; un-dismiss so a manually-closed
                        // panel re-opens on the next push.
                        setVoiceContent(prev => mergeVoiceSegments(prev, segs));
                        setPanelDismissed(false);
                    }
                    return;
                }

                if (message.parts) {
                    for (const part of message.parts) {
                        if (part.type === 'audio/pcm') {
                            playAudioChunk(part.data);
                        }
                        // Legacy transcript-marker path. Kept harmless: once the
                        // server-side `voice_cards` emit is deployed the model
                        // stops emitting markers, so this finds nothing. While
                        // both paths can briefly co-exist (pre-deploy), the
                        // de-dup below prevents a marker + a `voice_cards` push
                        // for the same product from rendering twice.
                        if (part.type === 'text' && part.data) {
                            const blocks = parseVoiceDockBlocks(part.data);
                            if (blocks.length > 0) {
                                setVoiceContent(prev => mergeVoiceSegments(prev, blocks));
                                setPanelDismissed(false);
                            }
                        }
                    }
                }
            };

            ws.onclose = (ev) => {
                // [1007 audit] Surface the close code/reason in the user's own
                // console so we can correlate a 1007 with their AudioContext
                // sampleRate and chunk-size logs above.
                console.log('[VoicePlayground] WS closed', { code: ev.code, reason: ev.reason, wasClean: ev.wasClean });
                // Preserve a structured-error status if the backend already
                // set one (also Ended from conversation_ended) — otherwise
                // fall back to the generic "Disconnected".
                setStatus(prev =>
                    (prev.includes('error') || prev.includes('ended') || prev === 'Ended')
                        ? prev
                        : 'Disconnected',
                );
                setIsConnected(false);
                stopAudioCapture();
                // Plan-LiveAvatarLifecycleAndCreditsFallback §A.2.2 —
                // catch the unclean-close credit-leak path.
                teardownLiveAvatar();
            };

            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
                setStatus('Error');
                // Plan-LiveAvatarLifecycleAndCreditsFallback §A.2.2 —
                // ws.onerror often precedes ws.onclose, but not always
                // on every browser. Idempotent teardown means we can
                // safely call here too without risking a double-stop.
                teardownLiveAvatar();
            };

        } catch (error) {
            console.error('Failed to start session:', error);
            setStatus('Failed to connect');
        }
    };

    const stopSession = () => {
        // Task #17 — per-step trace. We're hunting "model gets 3 turns
        // after resolve_conversation, credits keep burning". Each log
        // below should be greppable in DevTools / shipped session
        // recordings against the backend `[disconnect-chain]` series.
        console.log('[VoicePlayground.stopSession] enter', {
            wsState: websocketRef.current?.readyState,  // 0=CONNECTING 1=OPEN 2=CLOSING 3=CLOSED
            avatarConnected: liveAvatarRef.current?.isConnected,
            avatarConnecting: liveAvatarRef.current?.isConnecting,
            hasAudioContext: !!audioContextRef.current,
            hasPlaybackContext: !!playbackContextRef.current,
            hasMediaStream: !!mediaStreamRef.current,
        });

        if (websocketRef.current) {
            websocketRef.current.close();
            websocketRef.current = null;
            console.log('[VoicePlayground.stopSession] ws closed');
        }
        stopAudioCapture();
        console.log('[VoicePlayground.stopSession] mic capture stopped');
        if (audioContextRef.current) {
            audioContextRef.current.close();
            audioContextRef.current = null;
            console.log('[VoicePlayground.stopSession] audio context closed');
        }

        // Task #17 — ALWAYS call disconnect via the ref. The previous
        // `if (liveAvatar.isConnected || liveAvatar.isConnecting)`
        // gate read stale closure state from the render that
        // registered the WS handler, so a `conversation_ended` frame
        // arriving N turns later could silently skip teardown while
        // HeyGen kept streaming and burning credits. disconnect() is
        // idempotent (disconnectedRef guard inside the hook), so we
        // call unconditionally and let it no-op when already down.
        try {
            liveAvatarRef.current?.disconnect();
            console.log('[VoicePlayground.stopSession] avatar disconnect called via ref');
        } catch (err) {
            console.warn('[VoicePlayground.stopSession] avatar disconnect threw', err);
        }

        // Tear down plain-voice playback context
        if (playbackContextRef.current) {
            try { playbackContextRef.current.close(); } catch { /* ignore */ }
            playbackContextRef.current = null;
            playbackTimeRef.current = 0;
            console.log('[VoicePlayground.stopSession] playback context closed');
        }

        setIsConnected(false);
        setIsRecording(false);
        setStatus('Ready');
        // Reset fallback state so the NEXT Start Conversation respects
        // the current `avatarEnabled` prop (the user may have refreshed
        // their addon entitlement between sessions).
        avatarActiveRef.current = avatarEnabled;
        setAvatarActive(avatarEnabled);
        console.log('[VoicePlayground.stopSession] exit complete');
    };

    const startAudioCapture = async () => {
        if (!audioContextRef.current || !websocketRef.current) return;

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: INPUT_SAMPLE_RATE,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                },
            });
            mediaStreamRef.current = stream;

            const source = audioContextRef.current.createMediaStreamSource(stream);

            // [1007 fix] Worklet now resamples mic input from whatever the
            // browser handed us (48000 / 44100 / 16000) down to 16000 before
            // Int16 conversion. Two paths:
            //   - Integer ratio (e.g., 48000→16000 = 3.0): block-average
            //     decimation (lowpass-equivalent for our voice band).
            //   - Non-integer ratio (e.g., 44100→16000 ≈ 2.756): linear
            //     interpolation, with a fractional accumulator carried
            //     across `process()` calls so we don't drift between blocks.
            //   - Same-rate (16000): the integer-ratio=1 branch is a no-op
            //     pass-through.
            // After resampling we still emit 1600 samples (= 100 ms @ 16 kHz)
            // per chunk, which keeps the WS frame size at 3200 bytes and
            // matches what backend tells Gemini ("audio/pcm;rate=16000").
            const processorCode = `
                class RecorderProcessor extends AudioWorkletProcessor {
                    constructor(options) {
                        super();
                        const opts = (options && options.processorOptions) || {};
                        // \`sampleRate\` is a global in AudioWorklet scope
                        // (the context's actual rate). Use it as the
                        // fallback so we never end up with NaN ratios.
                        this.inputRate = opts.inputSampleRate || sampleRate;
                        this.outputRate = opts.outputSampleRate || 16000;
                        this.ratio = this.inputRate / this.outputRate;
                        this.isIntegerRatio = Math.abs(this.ratio - Math.round(this.ratio)) < 1e-9;
                        this.intRatio = Math.round(this.ratio);
                        this.buffer = [];
                        this.targetSamples = 1600; // 100ms @ 16kHz
                        // Carry the fractional read-position across blocks
                        // for non-integer ratios (44100 → 16000 etc.) so we
                        // don't develop drift / clicking between 128-sample
                        // input frames.
                        this.resampleAccumulator = 0;
                    }
                    process(inputs) {
                        const input = inputs[0];
                        if (input && input.length > 0) {
                            const float32Data = input[0];
                            if (this.isIntegerRatio && this.intRatio === 1) {
                                // Same-rate fast path (most Chrome installs).
                                for (let i = 0; i < float32Data.length; i++) {
                                    this.buffer.push(float32Data[i]);
                                }
                            } else if (this.isIntegerRatio) {
                                // Integer downsample (48000→16000 = 3:1).
                                // Average each window for a cheap lowpass.
                                for (let i = 0; i < float32Data.length; i += this.intRatio) {
                                    let sum = 0;
                                    let count = 0;
                                    const end = Math.min(i + this.intRatio, float32Data.length);
                                    for (let j = i; j < end; j++) {
                                        sum += float32Data[j];
                                        count++;
                                    }
                                    this.buffer.push(count > 0 ? sum / count : 0);
                                }
                            } else {
                                // Non-integer (44100→16000). Linear interp.
                                let outIdx = this.resampleAccumulator;
                                while (outIdx < float32Data.length) {
                                    const i0 = Math.floor(outIdx);
                                    const i1 = Math.min(i0 + 1, float32Data.length - 1);
                                    const frac = outIdx - i0;
                                    this.buffer.push(float32Data[i0] * (1 - frac) + float32Data[i1] * frac);
                                    outIdx += this.ratio;
                                }
                                this.resampleAccumulator = outIdx - float32Data.length;
                            }

                            while (this.buffer.length >= this.targetSamples) {
                                const chunk = this.buffer.slice(0, this.targetSamples);
                                this.buffer = this.buffer.slice(this.targetSamples);
                                const int16Data = new Int16Array(chunk.length);
                                for (let i = 0; i < chunk.length; i++) {
                                    const s = Math.max(-1, Math.min(1, chunk[i]));
                                    int16Data[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                                }
                                this.port.postMessage(int16Data.buffer, [int16Data.buffer]);
                            }
                        }
                        return true;
                    }
                }
                registerProcessor('recorder-processor', RecorderProcessor);
            `;

            const blob = new Blob([processorCode], { type: 'application/javascript' });
            const url = URL.createObjectURL(blob);
            await audioContextRef.current.audioWorklet.addModule(url);

            const worklet = new AudioWorkletNode(audioContextRef.current, 'recorder-processor', {
                processorOptions: {
                    inputSampleRate: audioContextRef.current.sampleRate,
                    outputSampleRate: INPUT_SAMPLE_RATE,
                },
            });
            workletNodeRef.current = worklet;

            worklet.port.onmessage = (event) => {
                if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
                    const bytes = new Uint8Array(event.data);
                    let binary = '';
                    for (let i = 0; i < bytes.byteLength; i++) {
                        binary += String.fromCharCode(bytes[i]);
                    }
                    websocketRef.current.send(JSON.stringify({
                        mime_type: 'audio/pcm',
                        data: btoa(binary),
                    }));
                    // [1007 audit] First chunk + every 100th (~10s cadence)
                    // confirms 3200-byte even-byte chunks. Drift here would
                    // point at sample-rate fallback or buffer corruption.
                    const n = chunkCountRef.current++;
                    if (n === 0 || n % 100 === 0) {
                        console.log('[VoicePlayground] chunk #' + n, 'bytes:', bytes.byteLength, 'wsState:', websocketRef.current.readyState);
                    }
                }
            };

            source.connect(worklet);
            const muteGain = audioContextRef.current.createGain();
            muteGain.gain.value = 0;
            worklet.connect(muteGain);
            muteGain.connect(audioContextRef.current.destination);

            setIsRecording(true);

        } catch (error) {
            console.error('Error accessing microphone:', error);
            setStatus('Mic Error');
        }
    };

    const stopAudioCapture = () => {
        if (mediaStreamRef.current) {
            mediaStreamRef.current.getTracks().forEach(track => track.stop());
            mediaStreamRef.current = null;
        }
        if (workletNodeRef.current) {
            workletNodeRef.current.disconnect();
            workletNodeRef.current = null;
        }
        setIsRecording(false);
    };

    const playAudioChunk = (base64Audio: string) => {
        // Route via the ref (live), not the prop — fallback flips the ref
        // synchronously when an avatar connect fails or the gate fires.
        if (avatarActiveRef.current) {
            liveAvatar.sendAudio(base64Audio);
            return;
        }
        // Plain voice: decode base64 PCM16 @ 24kHz and queue onto the
        // playback context. Lazy-init the context here (rather than in
        // startSession) so a frame that arrives BEFORE the gate-fallback
        // useEffect has propagated still has a destination — no silent
        // drop. Subsequent frames hit the existing context.
        let pctx = playbackContextRef.current;
        if (!pctx) {
            try {
                const PlaybackCtx = (window.AudioContext || (window as any).webkitAudioContext);
                pctx = new PlaybackCtx({ sampleRate: OUTPUT_SAMPLE_RATE });
                playbackContextRef.current = pctx;
                playbackTimeRef.current = pctx.currentTime;
            } catch (err) {
                console.error('Playback context init failed:', err);
                return;
            }
        }
        const float32 = base64PcmToFloat32(base64Audio);
        if (float32.length === 0) return;
        const buffer = pctx.createBuffer(1, float32.length, OUTPUT_SAMPLE_RATE);
        const channelData = buffer.getChannelData(0);
        for (let i = 0; i < float32.length; i++) channelData[i] = float32[i];
        const source = pctx.createBufferSource();
        source.buffer = buffer;
        source.connect(pctx.destination);
        const startAt = Math.max(playbackTimeRef.current, pctx.currentTime);
        source.start(startAt);
        playbackTimeRef.current = startAt + buffer.duration;
    };

    // Plan-VoiceWidgetRevisions Item 2 — an article is "docked beside" only
    // when reading one AND we can widen the iframe (embedded in widget.js).
    // Mobile / Playground preview / direct tab → overlay (handled inside
    // ArticleDockPanel by its own width detection; here it just affects the
    // avatar column width). The cards panel (when present, no article open)
    // sits beside the avatar on desktop too. In all these cases the avatar
    // column flexes to fill the remaining space so the face stays clear —
    // it no longer shrinks to a fixed 2/5.
    const hasSidePanel = (voiceContent.length > 0 && !panelDismissed) || viewingSlug !== null;

    return (
        <div className="absolute inset-0 flex">
            {/* Avatar / plain-voice container. Flexes to fill the space the
                cards / docked-article column leaves (was: shrank to w-2/5,
                which crowded the avatar face — Item 2). On mobile it stays
                full-width; cards are a bottom sheet and the article is a
                full overlay, neither steals horizontal space. */}
            <div className={`transition-all duration-300 ease-in-out h-full relative ${
                hasSidePanel ? 'w-full sm:flex-1 sm:min-w-0' : 'w-full'
            }`}>
                <div className={`absolute inset-0 flex flex-col items-center justify-center p-6 space-y-6 ${theme === 'dark' ? 'bg-black' : 'bg-white'}`}>
                    {/* Title */}
                    <div className="text-center space-y-1">
                        <h3 className={`text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Realtime Voice Agent</h3>
                        {/* Plan-EndConversationUX §B.3 — "Ended" is a
                            distinct quiet state (muted gray, no pulse)
                            alongside the existing Connected / Connecting
                            / Disconnected / Error pills. Reached when
                            the WS receives a `conversation_ended` frame
                            (auto-resolved by the agent), as a peer of
                            the existing error states but with neutral
                            tone — the session ended successfully, it
                            wasn't a failure. */}
                        <p className={`text-xs font-medium ${
                            status === 'Connected' ? 'text-green-500' :
                            status === 'Connecting...' ? 'text-blue-500' :
                            status === 'Ended' ? (theme === 'dark' ? 'text-gray-400' : 'text-gray-500') :
                            status.includes('error') || status.includes('ended') ? 'text-red-400' :
                            status.includes('failed') || status.includes('Failed') || status.includes('unavailable') ? 'text-yellow-400' :
                            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                        }`}>Status: {status}</p>
                    </div>

                    {avatarActive ? (
                        <div className="w-64 h-64 rounded-2xl overflow-hidden shadow-md relative bg-black">
                            <video
                                ref={liveAvatar.videoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                            />
                            {!liveAvatar.isConnected && !liveAvatar.isConnecting && (
                                <img
                                    src="/avatars/allison-preview.png"
                                    alt="AI Avatar preview"
                                    className="absolute inset-0 w-full h-full object-cover"
                                />
                            )}
                            {liveAvatar.isConnecting && (
                                <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80">
                                    <span className="text-sm text-blue-400 animate-pulse">Connecting to Agent</span>
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center gap-2">
                            <div
                                className={`w-24 h-24 rounded-full flex items-center justify-center shadow-md transition-all ${isRecording ? 'animate-pulse' : ''}`}
                                style={{ backgroundColor: isConnected ? brandColor : theme === 'dark' ? '#1f2937' : '#e5e7eb' }}
                            >
                                <Mic
                                    className="w-8 h-8"
                                    style={{ color: isConnected ? '#ffffff' : theme === 'dark' ? '#9ca3af' : '#6b7280' }}
                                />
                            </div>
                            {/* Plan-EffectiveLimitsAndMaintenanceBanners.md §2b —
                                degraded LA label. Voice tab works because voice
                                has its own budget; only the avatar overlay was
                                gated. Shown only when agent config wanted an
                                avatar but the workspace has no LA budget left
                                (`liveAvatarUnavailable`). Plain non-avatar
                                voice sessions never see this label. */}
                            {avatarEnabled && liveAvatarUnavailable && (
                                <p
                                    className={`text-[11px] italic ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}
                                    aria-live="polite"
                                >
                                    AI Avatar temporarily unavailable
                                </p>
                            )}
                        </div>
                    )}

                    {/* Start / End button */}
                    {!isConnected ? (
                        <button
                            onClick={startSession}
                            className="w-full max-w-xs flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold text-white shadow-lg transition-transform hover:scale-105"
                            style={{ backgroundColor: brandColor }}
                        >
                            <Play className="w-4 h-4" />
                            Start Conversation
                        </button>
                    ) : (
                        <button
                            onClick={stopSession}
                            className="w-full max-w-xs flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold text-white bg-red-500 hover:bg-red-600 shadow-lg transition-transform hover:scale-105"
                        >
                            <StopCircle className="w-4 h-4" />
                            End Session
                        </button>
                    )}

                    {/* Recording indicator */}
                    {isRecording && (
                        <div className="flex items-center gap-1.5">
                            <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                            <span className={`text-xs ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Listening</span>
                        </div>
                    )}

                    {/* AI Avatar error */}
                    {liveAvatar.error && (
                        <p className="text-xs text-red-400">{liveAvatar.error}</p>
                    )}

                    {/* Footer */}
                    <div className="text-center">
                        <span className="text-[9px] text-gray-400 block mb-2">Ensure you are in a quiet environment.</span>
                        <span className="text-[9px] text-gray-400">
                            Powered by{' '}
                            <a
                                href="https://askprodigy.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="underline hover:text-gray-300 dark:hover:text-gray-200"
                            >
                                AskProdigy AI
                            </a>
                        </span>
                    </div>
                </div>
            </div>

            {/* Plan-VoiceWidgetRevisions Item 2 — when the user is reading an
                article, render it BESIDE the avatar (docked column, desktop +
                embedded) or as a full-screen overlay (mobile / preview), via
                ArticleDockPanel. Otherwise show the compact cards panel.
                Showing one at a time mirrors the prior in-panel swap, but the
                article no longer shrinks the avatar to 2/5. */}
            {viewingSlug ? (
                <ArticleDockPanel
                    agentId={agentId}
                    slug={viewingSlug}
                    brandColor={brandColor}
                    isDark={theme === 'dark'}
                    embedded={embedded}
                    onBack={() => setViewingSlug(null)}
                />
            ) : (
                voiceContent.length > 0 && !panelDismissed && (
                    <VoiceContentPanel
                        content={voiceContent}
                        brandColor={brandColor}
                        theme={theme || 'light'}
                        // Dismiss = HIDE, not clear. Keep `voiceContent` so the
                        // agent can re-surface the same cards ("send it again");
                        // the next `voice_cards` batch flips `panelDismissed`
                        // back to false and re-opens the panel.
                        onClose={() => { setViewingSlug(null); setPanelDismissed(true); }}
                        onReadArticle={(slug) => setViewingSlug(slug)}
                    />
                )
            )}

            <LiveAvatarGateModal gate={liveAvatar.gate} onClose={liveAvatar.clearGate} />
        </div>
    );
};

export default VoicePlayground;
